import { createTheme } from '@mui/material';

// Brand tokens — single source of truth for the BlockWebAI palette.
export const INK = '#0B2E43';
export const INK_SOFT = '#16415C';
export const TEAL = '#13947F';
export const TEAL_DARK = '#0C6E5E';
export const GOLD = '#DFA23A';
export const SLATE = '#52677A';
export const SLATE_LIGHT = '#7A8FA0';
export const LINE = '#DFE8EE';
export const SURFACE_TINT = '#F7FAFC';
export const PAGE_BG = '#F3F6F8';

export const FONT_BODY = '"Inter", "SF Pro Text", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
export const FONT_DISPLAY = '"Sora", "Inter", "Segoe UI", sans-serif';

export const SHADOW_SM = '0 1px 2px rgba(11, 46, 67, 0.05), 0 4px 12px -4px rgba(11, 46, 67, 0.06)';
export const SHADOW_MD = '0 1px 2px rgba(11, 46, 67, 0.05), 0 16px 36px -18px rgba(11, 46, 67, 0.16)';
export const SHADOW_LG = '0 2px 4px rgba(11, 46, 67, 0.05), 0 28px 64px -24px rgba(11, 46, 67, 0.22)';

export const FOCUS_RING = '0 0 0 4px rgba(19, 148, 127, 0.18)';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0E4D6E',
      dark: '#093A55',
      light: '#3A7392',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: TEAL,
      dark: TEAL_DARK,
      light: '#41AC9A',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#178564',
      light: '#E4F4EE',
      dark: '#0F6B4F',
    },
    warning: {
      main: '#B97E1E',
      light: '#FBF3E2',
      dark: '#926212',
    },
    error: {
      main: '#C04531',
      light: '#FBEAE6',
      dark: '#9A3322',
    },
    info: {
      main: '#2F6F94',
      light: '#E8F1F7',
      dark: '#235877',
    },
    background: {
      default: PAGE_BG,
      paper: '#FFFFFF',
    },
    text: {
      primary: '#13293A',
      secondary: SLATE,
    },
    divider: LINE,
  },
  typography: {
    fontFamily: FONT_BODY,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      letterSpacing: '-0.03em',
      lineHeight: 1.08,
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 800,
      letterSpacing: '-0.025em',
      lineHeight: 1.12,
    },
    h3: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.18,
    },
    h4: {
      fontFamily: FONT_DISPLAY,
      fontWeight: 700,
      letterSpacing: '-0.015em',
      fontSize: '1.45rem',
      lineHeight: 1.25,
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
      fontSize: '1.15rem',
      lineHeight: 1.35,
    },
    h6: {
      fontWeight: 700,
      fontSize: '1rem',
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 700,
      lineHeight: 1.45,
    },
    body1: {
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.65,
    },
    overline: {
      fontWeight: 800,
      letterSpacing: '0.14em',
      fontSize: '0.72rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 700,
      letterSpacing: '0.005em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: PAGE_BG,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${LINE}`,
          boxShadow: SHADOW_SM,
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 10,
          paddingInline: 18,
          transition: 'transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',
        },
        sizeSmall: {
          minHeight: 32,
          paddingInline: 12,
          borderRadius: 8,
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          minHeight: 48,
          paddingInline: 26,
          fontSize: '1rem',
          borderRadius: 12,
        },
        containedPrimary: {
          background: 'linear-gradient(180deg, #11587D 0%, #0C425F 100%)',
          boxShadow: '0 1px 2px rgba(11,46,67,0.2), inset 0 1px 0 rgba(255,255,255,0.12)',
          '&:hover': {
            background: 'linear-gradient(180deg, #136189 0%, #0E4A6A 100%)',
            boxShadow: '0 6px 16px -6px rgba(11,46,67,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(180deg, #16A38C 0%, #0F7E66 100%)',
          boxShadow: '0 1px 2px rgba(12,110,94,0.25), inset 0 1px 0 rgba(255,255,255,0.16)',
          '&:hover': {
            background: 'linear-gradient(180deg, #18B098 0%, #11876F 100%)',
            boxShadow: '0 6px 16px -6px rgba(12,110,94,0.5), inset 0 1px 0 rgba(255,255,255,0.16)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': {
            borderWidth: 1.5,
          },
        },
        outlinedPrimary: {
          borderColor: '#C3D3DD',
          color: '#0E4D6E',
          '&:hover': {
            borderColor: '#0E4D6E',
            backgroundColor: 'rgba(14,77,110,0.05)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          transition: 'box-shadow 160ms ease',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D3DFE7',
            transition: 'border-color 160ms ease',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9FB4C2',
          },
          '&.Mui-focused': {
            boxShadow: FOCUS_RING,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TEAL,
            borderWidth: 1.5,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
        },
        sizeSmall: {
          fontSize: '0.72rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
          alignItems: 'center',
        },
        standardSuccess: {
          border: '1px solid rgba(23,133,100,0.25)',
        },
        standardError: {
          border: '1px solid rgba(192,69,49,0.25)',
        },
        standardWarning: {
          border: '1px solid rgba(185,126,30,0.28)',
        },
        standardInfo: {
          border: '1px solid rgba(47,111,148,0.25)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: SHADOW_LG,
          border: `1px solid ${LINE}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontWeight: 700,
          fontSize: '1.25rem',
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          border: `1px solid ${LINE}`,
          borderRadius: 14,
          boxShadow: 'none',
          overflow: 'hidden',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          paddingInline: 20,
          minHeight: 60,
          '&:hover': {
            backgroundColor: SURFACE_TINT,
          },
        },
        content: {
          marginBlock: 14,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingInline: 20,
          paddingBottom: 20,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: SURFACE_TINT,
            fontWeight: 700,
            color: SLATE,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            borderBottom: `1px solid ${LINE}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #EBF1F5',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: INK,
          borderRadius: 8,
          fontSize: '0.78rem',
          fontWeight: 600,
          paddingInline: 12,
          paddingBlock: 7,
        },
        arrow: {
          color: INK,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: 'rgba(11,46,67,0.08)',
        },
        bar: {
          borderRadius: 999,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${LINE}`,
          boxShadow: SHADOW_MD,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 6,
          paddingBlock: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(19,148,127,0.1)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: GOLD,
        },
      },
    },
  },
});

export default theme;
